/**
 * GarfieldLogger
 *
 * Sends events to Garfield SDK for analytics tracking.
 * Implements ILogger interface from EventManager.
 */

import {
	GarfieldCommands,
	IGarfieldEventSchema,
	IGarfieldDataConfig,
	IGarfieldEventPayload,
} from '@paylink/types';
import { DEFAULT_EVENT_VALUE, ANALYTICS_DATA_TAGS } from '@paylink/constants';
import { DataShareService } from '@paylink/services';

import { ILogger, ILoggerConfig, IEvent } from '@shared/services';
import { getTagsData, isEmpty } from '@shared/utils';

/** GarfieldLogger configuration */
export interface IGarfieldLoggerConfig extends ILoggerConfig {
	source?: string;
	meta?: Record<string, unknown>;
}

/** Prepare Garfield event data with tags */
const prepareGarfieldEventData = (
	garfieldEvent: IGarfieldDataConfig & { eventType: string },
): IGarfieldEventPayload => {
	const eventTags = garfieldEvent.additionalData ?? {};
	const tagsData = getTagsData(eventTags, ANALYTICS_DATA_TAGS);
	return {
		eventType: garfieldEvent.eventType as IGarfieldEventPayload['eventType'],
		eventName: garfieldEvent.eventName,
		value: garfieldEvent.value,
		data: { tags: { ...tagsData } },
	};
};

/** Ingest event to Garfield SDK */
const ingestToGarfield = (eventSchema: IGarfieldEventSchema): void => {
	const { eventData, eventType } = eventSchema;
	const { eventName, value, additionalData } = eventData;

	const eventObj = {
		eventType,
		eventName,
		value,
		additionalData,
	};

	try {
		const finalEventData = prepareGarfieldEventData(eventObj);
		window.garfield?.(GarfieldCommands.SEND, finalEventData);
	} catch (error) {
		console.error('Garfield ingestion error:', error);
	}
};

/** Process a single Garfield event */
const processGarfieldEvent = (
	eventName: string,
	data?: Record<string, unknown>,
): void => {
	const eventType = (data?.eventType as string) ?? 'custom';
	const garfieldData = (data?.garfieldData as Record<string, unknown>) ?? {};
	const value = data?.value ?? DEFAULT_EVENT_VALUE;

	try {
		const eventData: IGarfieldDataConfig = {
			eventName,
			additionalData: garfieldData,
			value: isEmpty(value) ? DEFAULT_EVENT_VALUE : value,
		};

		ingestToGarfield({
			eventType: eventType as IGarfieldEventSchema['eventType'],
			eventData,
		});
	} catch (error) {
		console.error('Garfield event processing error:', error);
	}
};

/** Garfield event queue for events before SDK is ready */
let garfieldQueue: Array<{
	eventName: string;
	data?: Record<string, unknown>;
}> = [];

/** Process queued events when Garfield becomes ready */
const processQueuedEvents = (): void => {
	if (garfieldQueue.length > 0) {
		garfieldQueue.forEach((item) => {
			processGarfieldEvent(item.eventName, item.data);
		});
		garfieldQueue = [];
	}
};

/** Default configuration */
const DEFAULT_CONFIG: Partial<IGarfieldLoggerConfig> = {
	batchSize: 1, // Garfield events are sent immediately
	flushMs: 0,
	source: 'PAYLINK',
};

/**
 * Creates a GarfieldLogger instance
 *
 * @param config - Logger configuration
 * @returns ILogger instance for Garfield
 */
export const createGarfieldLogger = (
	config: IGarfieldLoggerConfig,
): ILogger<IGarfieldLoggerConfig> => {
	const finalConfig: IGarfieldLoggerConfig = {
		...DEFAULT_CONFIG,
		...config,
	};

	return {
		config: finalConfig,

		process(event: IEvent): IEvent {
			const orderId = DataShareService.paylinkDetail?.orderId ?? '';

			return {
				...event,
				data: {
					...event.data,
					source: finalConfig.source,
					...finalConfig.meta,
					garfieldData: {
						...(event.data?.garfieldData as Record<string, unknown>),
						orderId,
					},
				},
			};
		},

		/**
		 * Send events to Garfield SDK
		 * Handles queueing if Garfield is not yet ready
		 */
		async send(events: IEvent[]): Promise<void> {
			for (const event of events) {
				const eventName = event.name;
				const eventData = event.data;

				if (!window.isGarfieldReady) {
					garfieldQueue.push({ eventName, data: eventData });
				} else {
					processQueuedEvents();
					processGarfieldEvent(eventName, eventData);
				}
			}
		},
	};
};
